import { useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

import {
  IconFlightService,
  IconHotelService,
  IconTripService,
} from "@/components/Icons";
import {
  FlightSearchTrackingEvents,
  HotelSearchTrackingEvents,
  rudderAnalytics,
  TripTrackingEvents,
} from "@/lib/rudderstack";
import type { SearchScope } from "@/types/types";
import { Feature, isFeatureEnabled } from "@/utils/featureFlags";

import { SearchNavigationButton } from "./SearchNavigationButton";

const eventMap = {
  flights: FlightSearchTrackingEvents.USER_SWITCHED_TO_FLIGHT_SEARCH,
  hotels: HotelSearchTrackingEvents.USER_SWITCHED_TO_HOTEL_SEARCH,
  trips: TripTrackingEvents.USER_SWITCHED_TO_TRIPS,
} as const;

export const SearchNavigationButtons = () => {
  const t = useTranslations();

  const navigate = useNavigate();

  const handleClick = (scope: SearchScope) => {
    if (eventMap[scope]) {
      rudderAnalytics.track(eventMap[scope]);
    }

    if (scope === "flights") {
      navigate({
        to: "/flights/search",
      });
    } else if (scope === "hotels") {
      navigate({
        to: "/hotels/search",
      });
    } else if (scope === "trips") {
      navigate({
        to: "/trips/search",
      });
    }
  };

  return (
    <div className="flex gap-6">
      <SearchNavigationButton onClick={() => handleClick("flights")}>
        <IconFlightService aria-hidden className="size-9 shrink-0" />
        {t("flights.title")}
      </SearchNavigationButton>

      {isFeatureEnabled(Feature.HotelsFlow) && (
        <SearchNavigationButton onClick={() => handleClick("hotels")}>
          <IconHotelService aria-hidden className="size-9 shrink-0" />
          {t("hotels.title")}
        </SearchNavigationButton>
      )}

      {isFeatureEnabled(Feature.TripsFlow) && (
        <SearchNavigationButton
          onClick={() => handleClick("trips")}
          badgeContent={t("trips.newBadge")}
        >
          <IconTripService aria-hidden className="size-9 shrink-0" />
          {t("trips.title")}
        </SearchNavigationButton>
      )}
    </div>
  );
};
